import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from './ui/basicos';
import { Dialogo } from './ui/dialogo';
import { Campo, Interruptor } from './ui/formulario';
import { useTextoDeError } from './ui/estados';

export interface Corregible {
  readonly id: string;
  readonly nombre: string;
  readonly codigo?: string;
  readonly activo?: boolean;
}

/**
 * Corregir el nombre —y el código, cuando tiene— de algo de la estructura.
 *
 * **Uno solo para depósito, puesto de caja, punto de venta y lista de precios.** Los cuatro son la
 * misma corrección con otra ruta; cuatro diálogos idénticos serían cuatro lugares donde arreglar el
 * mismo defecto, y el cuarto siempre se olvida.
 *
 * Lo que **no** ofrece es tan importante como lo que ofrece:
 *
 * - **La sucursal de un depósito.** Moverlo de local haría que su stock aparezca en otra sucursal
 *   sin que nada se haya movido físicamente. Eso es una transferencia.
 * - **El número de un punto de venta.** De ahí cuelga la numeración de los comprobantes y ante el
 *   organismo es su identidad.
 * - **La moneda de una lista de precios.** Los precios cargados están expresados en esa moneda;
 *   cambiarla reinterpretaría cada número sin tocar ninguno.
 *
 * El servidor los rechaza igual —los esquemas de corrección son estrictos—, pero no ofrecerlos
 * evita que alguien los busque.
 */
export function CorregirNombre({
  que,
  ruta,
  conCodigo = true,
  conActivo = true,
  alGuardar,
  onCerrar,
}: {
  readonly que: Corregible | null;
  readonly ruta: string;
  readonly conCodigo?: boolean;
  readonly conActivo?: boolean;
  readonly alGuardar: readonly string[];
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  /**
   * Lo escrito, con lo que había abajo.
   *
   * **No se copian los valores al estado cuando se abre**, que es lo primero que uno intenta: eso
   * exige un efecto que sincronice props con estado, y sincronizar en un efecto dispara
   * redibujados en cascada —lo rechaza el lint, y con razón—. Guardar sólo lo que la persona tocó
   * y leer el resto de la fila resuelve las dos cosas: no hay efecto y, mientras alguien escribe,
   * un refresco de la lista de atrás no le pisa lo que está tipeando.
   */
  const [tocado, setTocado] = useState<{ nombre?: string; codigo?: string; activo?: boolean }>({});
  const [error, setError] = useState<string | null>(null);

  const nombre = tocado.nombre ?? que?.nombre ?? '';
  const codigo = tocado.codigo ?? que?.codigo ?? '';
  const activo = tocado.activo ?? que?.activo ?? true;

  const cerrar = (): void => {
    setTocado({});
    setError(null);
    onCerrar();
  };

  const guardar = useMutation({
    mutationFn: () => {
      const cambios: Record<string, string | boolean> = {};
      if (que !== null) {
        if (nombre.trim() !== que.nombre) cambios.nombre = nombre.trim();
        if (conCodigo && codigo.trim() !== (que.codigo ?? '')) cambios.codigo = codigo.trim();
        if (conActivo && activo !== (que.activo ?? true)) cambios.activo = activo;
      }
      return pedir<void>(`${ruta}/${que?.id ?? ''}`, { method: 'PATCH', cuerpo: cambios });
    },
    onSuccess: async () => {
      setTocado({});
      setError(null);
      onCerrar();
      for (const clave of alGuardar) await cola.invalidateQueries({ queryKey: [clave] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Dialogo
      abierto={que !== null}
      titulo={t('estructura.corregir')}
      onCerrar={cerrar}
      acciones={
        <Boton
          tono="principal"
          cargando={guardar.isPending}
          onClick={() => {
            guardar.mutate();
          }}
        >
          {t('acciones.guardar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-3">
        {!conCodigo ? null : (
          <Campo
            etiqueta={t('estructura.codigo')}
            value={codigo}
            onChange={(evento) => {
              setTocado({ ...tocado, codigo: evento.target.value });
            }}
          />
        )}
        <Campo
          etiqueta={t('estructura.nombre')}
          value={nombre}
          onChange={(evento) => {
            setTocado({ ...tocado, nombre: evento.target.value });
          }}
        />
        {!conActivo ? null : (
          <Interruptor
            etiqueta={t('estructura.enUso')}
            checked={activo}
            onChange={(evento) => {
              setTocado({ ...tocado, activo: evento.target.checked });
            }}
          />
        )}
        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
